import DetailTemplate from '@/components/detail-template'
import React, { useEffect, useMemo, useCallback } from 'react'
import { displayModel } from './display-model'
import { useTaskDetailHooks, useTaskDetailState } from './hooks'
import { useTaskDetailHandlers } from './handlers'
import { createTableColumns } from './table-config'
import { importModalOptions } from './constants'
import styled from 'styled-components'
import { DeleteModal, TagSearchOption, ImportModal, RecordModal } from '@/components'
import TaskCharts from './task-charts'
import { DetailDrawer } from './detail-drawer'
import { CreateReport } from '@/components/create-report'
import TaskContentComponent from './task-content'
import TaskContentExpandedComponent from './task-content-expanded'
import { t } from '@/i18n'

// 提取数据获取逻辑
const useTaskDetailData = () => {
  const { taskInfoExpanded, taskInfo: taskInfoModel } = displayModel
  const apiHooks = useTaskDetailHooks()
  const state = useTaskDetailState()

  return {
    taskInfoExpanded,
    taskInfoModel,
    apiHooks,
    state,
  }
}

// 提取计算逻辑
const useTaskDetailComputed = (taskInfo: any, taskInfoModel: any[]) => {
  const convertTaskInfo = useMemo(
    () => ({
      ...taskInfo,
      progressRate:
        String(taskInfo?.isFinished) === '3' || String(taskInfo?.isFinished) === '4'
          ? 100
          : String(taskInfo?.isFinished) === '2'
          ? 50
          : 0,
    }),
    [taskInfo],
  )

  const taskContentComponent = useMemo(
    () => (
      <TaskContentComponent
        taskInfoModel={taskInfoModel}
        convertTaskInfo={convertTaskInfo}
        taskInfo={taskInfo}
      />
    ),

    [taskInfoModel, convertTaskInfo, taskInfo],
  )

  return {
    convertTaskInfo,
    taskContentComponent,
  }
}

// 提取API hooks解构逻辑
const useTaskDetailApiHooks = (apiHooks: any) => {
  const {
    form,
    id,
    setId,
    taskId,
    getTaskInfo,
    taskInfo,
    getAttackType,
    attackType,
    getAttck,
    attckList,
    getAPTList,
    getUseCaseList,
    loading,
    updateResourceInterceptStatus,
    updateInterceptStatusLoading,
    getTagTreeOptions,
    tagTreeOptions,
    getOfflineLogInfo,
    importRecord,
    getResourceFilterData,
    resourceFilterData,
    getDeviceProtectionList,
    deviceProtectionList,
    getAttackDefenseRanking,
    attackDefenseRanking,
    getRiskLevelDistribution,
    riskLevelDistribution,
    getResourceLevelsRanking,
    resourceLevelsRanking,
    getTemplateLoseEfficacyRanking,
    templateLoseEfficacyRanking,
    getVerifyDistribution,
    verifyDistribution,
    generateReport,
    createLoading,
    deleteTask,
    getResultPluginDetail,
    detailLoading,
    deleteRecordLoading,
    deleteOfflineLogInfo,
  } = apiHooks

  return {
    form,
    id,
    setId,
    taskId,
    getTaskInfo,
    taskInfo,
    getAttackType,
    attackType,
    getAttck,
    attckList,
    getAPTList,
    getUseCaseList,
    loading,
    updateResourceInterceptStatus,
    updateInterceptStatusLoading,
    getTagTreeOptions,
    tagTreeOptions,
    getOfflineLogInfo,
    importRecord,
    getResourceFilterData,
    resourceFilterData,
    getDeviceProtectionList,
    deviceProtectionList,
    getAttackDefenseRanking,
    attackDefenseRanking,
    getRiskLevelDistribution,
    riskLevelDistribution,
    getResourceLevelsRanking,
    resourceLevelsRanking,
    getTemplateLoseEfficacyRanking,
    templateLoseEfficacyRanking,
    getVerifyDistribution,
    verifyDistribution,
    generateReport,
    createLoading,
    deleteTask,
    getResultPluginDetail,
    detailLoading,
    deleteRecordLoading,
    deleteOfflineLogInfo,
  }
}

// 提取状态解构逻辑
const useTaskDetailStateHooks = (state: any) => {
  const {
    logInfo,
    setLogInfo,
    failLogInfo,
    setFailLogInfo,
    filterParams,
    setFilterParams,
    filterFormValue,
    setFilterFormValue,
    useCaseList,
    setUseCaseList,
    useCaseTotal,
    setUseCaseTotal,
    fixPayloadCount,
    setFixPayloadCount,
    selectedRowKeys,
    setSelectedRowKeys,
    setRandom,
    setCardActive,
    importVisible,
    setImportVisible,
    setRecordVisible,
    recordVisible,
    reportVisible,
    setReportVisible,
    setActive,
    deleteVisible,
    setDeleteVisible,
    detailDrawerVisible,
    setDetailDrawerVisible,
    pluginDetail,
    setPluginDetail,
    fixedModalVisible,
    setFixedModalVisible,
    riskLevelSelectOpen,
    setRiskLevelSelectOpen,
    attackTypeSelectOpen,
    setAttackTypeSelectOpen,
    resourceLevelSelectOpen,
    setResourceLevelSelectOpen,
    interceptStatusSelectOpen,
    setInterceptStatusSelectOpen,
    deviceStatusSelectOpen,
    setDeviceStatusSelectOpen,
    infoActiveTab,
    setInfoActiveTab,
    activeTab,
    setActiveTab,
    executeTimeId,
    setExecuteTimeId,
    setId,
  } = state

  return {
    logInfo,
    setLogInfo,
    failLogInfo,
    setFailLogInfo,
    filterParams,
    setFilterParams,
    filterFormValue,
    setFilterFormValue,
    useCaseList,
    setUseCaseList,
    useCaseTotal,
    setUseCaseTotal,
    fixPayloadCount,
    setFixPayloadCount,
    selectedRowKeys,
    setSelectedRowKeys,
    setRandom,
    setCardActive,
    importVisible,
    setImportVisible,
    setRecordVisible,
    recordVisible,
    reportVisible,
    setReportVisible,
    setActive,
    deleteVisible,
    setDeleteVisible,
    detailDrawerVisible,
    setDetailDrawerVisible,
    pluginDetail,
    setPluginDetail,
    fixedModalVisible,
    setFixedModalVisible,
    riskLevelSelectOpen,
    setRiskLevelSelectOpen,
    attackTypeSelectOpen,
    setAttackTypeSelectOpen,
    resourceLevelSelectOpen,
    setResourceLevelSelectOpen,
    interceptStatusSelectOpen,
    setInterceptStatusSelectOpen,
    deviceStatusSelectOpen,
    setDeviceStatusSelectOpen,
    infoActiveTab,
    setInfoActiveTab,
    activeTab,
    setActiveTab,
    executeTimeId,
    setExecuteTimeId,
    setId,
  }
}

// 提取任务评分逻辑
const useTaskScore = () =>
  useMemo(
    () => ({
      score: 0,
      risk_level: '',
    }),
    [],
  )

// 提取图表数据获取逻辑
const useEchartsData = (apiData: any) =>
  useCallback(
    (result_id: string | undefined = apiData.id) => {
      apiData.getAttackDefenseRanking({ resultId: result_id }).catch(() => {})
      apiData.getRiskLevelDistribution({ resultId: result_id }).catch(() => {})
      apiData.getResourceLevelsRanking({ resultId: result_id }).catch(() => {})
      apiData.getTemplateLoseEfficacyRanking({ resultId: result_id }).catch(() => {})
      apiData.getVerifyDistribution({ resultId: result_id }).catch(() => {})
      apiData.getDeviceProtectionList({ resultId: result_id }).catch(() => {})
    },
    [apiData],
  )

// 提取刷新请求逻辑
const useRefreshRequest = (apiData: any, stateData: any) =>
  useCallback(
    (result_id = apiData.id) => {
      apiData.getTaskInfo({ result_id, task_id: apiData.taskId }).catch(() => {})
      apiData.getAttackType({ resultId: result_id }).catch(() => {})
      apiData.getAttck({ result_id, task_id: apiData.taskId }).catch(() => {})
      apiData.getTagTreeOptions({ result_id: result_id }).catch(() => {})
      apiData.getOfflineLogInfo({ resultId: result_id }).catch(() => {})

      stateData.setRandom(new Date().getTime())
      stateData.setFilterParams({ page: 1, page_size: 20 })
    },
    [apiData, stateData],
  )

// 提取主组件逻辑
const useDetailLogic = () => {
  const { taskInfoExpanded, taskInfoModel, apiHooks, state } = useTaskDetailData()
  const apiData = useTaskDetailApiHooks(apiHooks)
  const stateData = useTaskDetailStateHooks(state)

  const taskScore = useTaskScore()
  const getEchartsData = useEchartsData(apiData)
  const refreshRequest = useRefreshRequest(apiData, stateData)

  return {
    taskInfoExpanded,
    taskInfoModel,
    apiData,
    stateData,
    taskScore,
    getEchartsData,
    refreshRequest,
  }
}

// 提取失败用例列表逻辑
const useFailedCaseList = (apiData: any) =>
  useMemo(
    () =>
      apiData.taskInfo?.execFailItems?.map((item: any, index: number) => ({
        execLog: item.failedReason,
        pluginName: item.caseName,
        execTime: item.createTime,
        id: index,
      })),
    [apiData.taskInfo],
  )

const Detail: React.FC = () => {
  const {
    taskInfoExpanded,
    taskInfoModel,
    apiData,
    stateData,
    taskScore,
    getEchartsData,
    refreshRequest,
  } = useDetailLogic()

  const failedCaseList = useFailedCaseList(apiData)

  // 使用事件处理函数
  const handlers = useTaskDetailHandlers(
    apiData.id,
    apiData.taskId,
    stateData.setExecuteTimeId,
    getEchartsData,
    refreshRequest,
    apiData.generateReport,
    apiData.deleteTask,
    stateData.setFailLogInfo,
    apiData.updateResourceInterceptStatus,
    stateData.setRandom,
    stateData.setSelectedRowKeys,
    apiData.getUseCaseList,
    stateData.setUseCaseList,
    stateData.setUseCaseTotal,
    stateData.setFixPayloadCount,
    apiData.getResultPluginDetail,
    stateData.setPluginDetail,
    stateData.setDetailDrawerVisible,
    stateData.setCardActive,
    failedCaseList,
    apiData.setId,
  )

  // 先计算 TagSearchOption 的结果
  const tagSearchOptions = TagSearchOption(apiData.tagTreeOptions)

  const cascaderOptions = useMemo(
    () =>
      apiData.attckList.map(stage => ({
        value: stage.id,
        label: stage.name,
        children: stage.technology.map(tech => ({
          value: tech.id,
          label: tech.name,
          children: tech.subTechnology
            .filter(subTech => subTech.id !== '0')
            .map(subTech => ({
              value: subTech.id,
              label: subTech.name,
            })),
        })),
      })),
    [apiData.attckList],
  )

  const options = useMemo(
    () => [
      {
        label: t('用例名称'),
        value: 'keyword',
        type: 'input' as const,
      },
      {
        label: 'ATT&CK',
        value: 'att_ck',
        type: 'cascader',
        mode: 'multiple',
        options: cascaderOptions,
      },
      {
        label: t('用例标签'),
        value: 'tag_ids',
        type: 'treeSelect',
        mode: 'multiple',
        options: tagSearchOptions,
      },
      {
        label: t('模板名称'),
        value: 'template_ids',
        type: 'select',
        mode: 'multiple',
        options: [
          ...(apiData.resourceFilterData?.templates || []).map(item => ({
            label: item.fieldName,
            value: item.fieldId,
          })),
        ],
      },
      {
        label: t('攻击组织'),
        value: 'apt_name',
        type: 'select',
        mode: 'multiple',
        options: apiData.resourceFilterData?.attackOrganizes?.map(item => ({
          label: item.fieldName,
          value: item.fieldName,
        })),
      },
      {
        label: t('置信度'),
        value: 'confidence_level',
        type: 'select',
        options: [
          { label: t('低'), value: '1' },
          { label: t('中'), value: '2' },
          { label: t('高'), value: '3' },
        ],
      },
      {
        label: t('协议类型'),
        value: 'protocol',
        type: 'select',
        options: [
          { label: 'DNS', value: '1' },
          { label: 'HTTP', value: '2' },
          { label: 'TCP', value: ' 3' },
          { label: 'HTTPS', value: '4' },
        ],
      },
    ],

    [apiData.attckList, tagSearchOptions, apiData.resourceFilterData],
  )
  // 使用提取的表格列配置
  const columns = createTableColumns(
    apiData.taskInfo,
    apiData.attackType,
    handlers.getDrawerData,
    stateData.setActive,
    stateData.riskLevelSelectOpen,
    stateData.setRiskLevelSelectOpen,
    stateData.attackTypeSelectOpen,
    stateData.setAttackTypeSelectOpen,
    stateData.resourceLevelSelectOpen,
    stateData.setResourceLevelSelectOpen,
    stateData.interceptStatusSelectOpen,
    stateData.setInterceptStatusSelectOpen,
    stateData.deviceStatusSelectOpen,
    stateData.setDeviceStatusSelectOpen,
    stateData.filterParams,
  )

  const rowSelection = {
    selectedRowKeys: stateData.selectedRowKeys,
    onSelect: (record, selected) => {
      let newSelectedRowKeys = [...stateData.selectedRowKeys]
      if (selected) {
        newSelectedRowKeys.push(record.id)
      } else {
        newSelectedRowKeys = newSelectedRowKeys.filter(key => key !== record.id)
      }
      stateData.setSelectedRowKeys(newSelectedRowKeys)
    },
    onSelectAll: selected => {
      const newSelectedRowKeys = selected ? stateData.useCaseList.map(item => item.id) : []
      stateData.setSelectedRowKeys(newSelectedRowKeys)
    },
  }

  // 使用提取的事件处理函数
  const { handleToggleTime, handleBack, createReport, handleDelete, handleExpand, handleFixed } =
    handlers

  const riskLevelMap = {
    1: { label: t('低危'), class: 'leak-low', key: 'RiskLevelLow' },
    2: { label: t('中危'), class: 'leak-center', key: 'RiskLevelMiddle' },
    3: { label: t('高危'), class: 'leak-high', key: 'RiskLevelHigh' },
    4: { label: t('严重'), class: 'leak-severity', key: 'RiskLevelGross' },
  }

  const resourceLevelMap = {
    1: { label: t('初级'), class: 'resource-level-junior', key: 'ResourceLevelJunior' },
    2: { label: t('中级'), class: 'resource-level-middle', key: 'ResourceLevelMiddle' },
    3: { label: t('高级'), class: 'resource-level-senior', key: 'ResourceLevelSenior' },
  }

  const interceptStatusMap = {
    1: {
      label: t('拦截成功'),
      class: 'intercept-success',
      key: 'intercept_status_intercept_success',
    },
    2: {
      label: t('防护失效'),
      class: 'intercept-fail',
      key: 'intercept_status_intercept_defence_fail',
    },
    3: { label: t('检测成功'), class: 'intercept-cancel', key: 'intercept_status_detect_success' },
  }

  const tagTreeData = useMemo(() => {
    const listMappings = [
      {
        title: t('用例分级'),
        list:
          apiData.resourceFilterData?.caseLevelList?.map((item: any) => ({
            ...item,
            fieldId: resourceLevelMap[item.fieldId]?.key || undefined,
          })) || [],
        total: apiData.resourceFilterData?.caseLevelTotal || 0,
        key: 'resource_level',
      },
      {
        title: t('危险等级'),
        list:
          apiData.resourceFilterData?.riskLevelList?.map((item: any) => ({
            ...item,
            fieldId: riskLevelMap[item.fieldId]?.key || undefined,
          })) || [],
        total: apiData.resourceFilterData?.riskLevelTotal || 0,
        key: 'tree_risk_level',
      },
      {
        title: t('验证结果'),
        list:
          apiData.resourceFilterData?.resourceStatusList?.map((item: any) => ({
            ...item,
            fieldId: interceptStatusMap[item.fieldId]?.key || undefined,
          })) || [],
        total: apiData.resourceFilterData?.resourceStatusTotal || 0,
        key: 'tree_intercept_status',
      },
      {
        title: t('安全设备'),
        list: apiData.resourceFilterData?.deviceList || [],
        total: apiData.resourceFilterData?.deviceTotal || 0,
        key: 'tree_device_ids',
      },
    ]

    return listMappings.map(category => ({
      title: category.title,
      key: category.key,
      extra: category.total,
      children: category.list.map(item => ({
        title: item.fieldName,
        key: `${category.key}_${item.fieldId}`,
        extra: item.resourceCount,
      })),
    }))
  }, [apiData.resourceFilterData])

  // 处理筛选参数，移除标识符
  const processFilterParams = (params: any) => {
    const processedParams = { ...params }
    const keyMapping = {
      tree_resource_level: 'resource_level',
      tree_risk_level: 'risk_level',
      tree_intercept_status: 'intercept_status',
      tree_device_ids: 'device_ids',
    }

    Object.keys(processedParams).forEach(key => {
      if (keyMapping[key]) {
        processedParams[keyMapping[key]] = processedParams[key]
        delete processedParams[key]
      }
    })

    return processedParams
  }

  useEffect(() => {
    apiData
      .getTaskInfo({ result_id: apiData.id, task_id: apiData.taskId })
      .then(res => {
        stateData.setReportVisible(prev => ({ ...prev, name: res.data.taskName }))
      })
      .catch(_ => {})
    apiData.getAttackType({ resultId: apiData.id }).catch(_ => {})
    apiData.getAttck({ result_id: apiData.id, task_id: apiData.taskId }).catch(_ => {})
    apiData.getTagTreeOptions({ result_id: apiData.id }).catch(_ => {})
    apiData.getResourceFilterData({ resultId: apiData.id }).catch(_ => {})
    apiData.getOfflineLogInfo({ resultId: apiData.id }).catch(_ => {})
  }, [])

  // 下拉筛选
  useEffect(() => {
    if (stateData.filterFormValue?.field) {
      console.log('filterFormValue', stateData.filterFormValue)
      const selectedOption = options.find(opt => opt.value === stateData.filterFormValue.field)
      if (selectedOption) {
        const processedValue =
          stateData.filterFormValue?.field === 'tag_ids'
            ? stateData.filterFormValue?.value === ''
              ? null
              : (stateData.filterFormValue?.value as string[])?.map(item =>
                  item.split('$').slice(1).join('$'),
                )
            : stateData.filterFormValue?.value
        stateData.setFilterParams(prev => {
          // 检查当前字段的值是否真的发生了变化
          if (prev[stateData.filterFormValue.field] === processedValue) {
            return prev
          }
          const dropdownKeys = options.map(opt => opt.value)
          const updatedParams = {
            ...prev,
            page: 1,
            [stateData.filterFormValue.field]: processedValue,
          }
          // 清除其他下拉筛选条件
          dropdownKeys.forEach(key => {
            if (key !== stateData.filterFormValue.field && key in updatedParams) {
              delete updatedParams[key]
            }
          })
          return updatedParams
        })
      }
    }
  }, [stateData.filterFormValue])

  useEffect(() => {
    const params = { ...stateData.filterParams }
    if (params.limit) {
      params.page_size = params.limit
      delete params.limit
    }
    const processedParams = processFilterParams(params)
    apiData
      .getUseCaseList({ result_id: apiData.id, task_id: apiData.taskId, ...processedParams })
      .then(res => {
        stateData.setUseCaseList(res.data.items)
        stateData.setUseCaseTotal(res.data.show_total)
        stateData.setFixPayloadCount(res.data.manual_correct_count)
      })
      .catch(_ => {})
  }, [stateData.filterParams])

  useEffect(() => {
    let intervalId
    if (apiData.importRecord.importing) {
      intervalId = setInterval(() => {
        apiData
          .getOfflineLogInfo({ resultId: apiData.id })
          .then(res => {
            if (!res.data.importing) {
              refreshRequest()
              getEchartsData()
            }
          })
          .catch(_ => {})
      }, 3000)
    }

    return () => clearInterval(intervalId)
  }, [apiData.importRecord])

  useEffect(() => {
    if (stateData.executeTimeId) return
    stateData.setExecuteTimeId(apiData.taskInfo?.executionList?.[0]?.resultId)
  }, [apiData.taskInfo])

  const fixedOptions = useMemo(
    () =>
      apiData.taskInfo?.devices?.length > 0
        ? [
            { label: t('拦截成功'), value: 'intercept_status_intercept_success' },
            { label: t('防护失效'), value: 'intercept_status_intercept_defence_fail' },
            { label: t('检测成功'), value: 'intercept_status_detect_success' },
          ]
        : [
            { label: t('拦截成功'), value: 'intercept_status_intercept_success' },
            { label: t('防护失效'), value: 'intercept_status_intercept_defence_fail' },
          ],

    [apiData.taskInfo],
  )

  const progressInfo = useMemo(
    () => ({
      ...apiData.taskInfo,
    }),
    [apiData.taskInfo],
  )

  const unsentCaseList = useMemo(
    () =>
      apiData.taskInfo?.noExecItems?.map((item: any, index: number) => ({
        execLog: item.failedReason,
        pluginName: item.caseName,
        execTime: item.createTime,
        id: index,
      })),
    [apiData.taskInfo],
  )

  // 使用 useMemo 来稳定 progressContentList 的值
  const stableProgressContentList = useMemo(() => {
    if (stateData.activeTab === 'failed') {
      return failedCaseList || []
    } else if (stateData.activeTab === 'unexecuted') {
      return unsentCaseList || []
    }
    return []
  }, [stateData.activeTab, failedCaseList, unsentCaseList])

  // 稳定的点击处理函数
  const handleFailedTabClick = useCallback(() => {
    stateData.setActiveTab('failed')
  }, [stateData.setActiveTab])

  const handleUnexecutedTabClick = useCallback(() => {
    stateData.setActiveTab('unexecuted')
  }, [stateData.setActiveTab])

  const deleteRecord = (importRecordId: string) => {
    apiData
      .deleteOfflineLogInfo({ resultId: apiData.id, id: importRecordId })
      .then(() => {
        refreshRequest()
        getEchartsData()
      })
      .catch(_ => {})
  }

  const { taskContentComponent } = useTaskDetailComputed(apiData.taskInfo, taskInfoModel)

  const taskContentExpanded = useMemo(
    () => (
      <TaskContentExpandedComponent
        taskInfo={apiData.taskInfo}
        taskInfoExpanded={taskInfoExpanded}
        executeTimeId={stateData.executeTimeId}
        infoActiveTab={stateData.infoActiveTab}
        activeTab={stateData.activeTab}
        logInfo={stateData.logInfo}
        failLogInfo={stateData.failLogInfo}
        progressInfo={progressInfo}
        failedCaseList={failedCaseList}
        unsentCaseList={unsentCaseList}
        stableProgressContentList={stableProgressContentList}
        onToggleTime={handleToggleTime}
        onSetInfoActiveTab={stateData.setInfoActiveTab}
        onSetActiveTab={stateData.setActiveTab}
        onSetLogInfo={stateData.setLogInfo}
        onFailedTabClick={handleFailedTabClick}
        onUnexecutedTabClick={handleUnexecutedTabClick}
      />
    ),

    [
      apiData.taskInfo,
      taskInfoExpanded,
      stateData.executeTimeId,
      stateData.infoActiveTab,
      stateData.activeTab,
      stateData.logInfo,
      stateData.failLogInfo,
      progressInfo,
      failedCaseList,
      unsentCaseList,
      stableProgressContentList,
      handleToggleTime,
      stateData.setInfoActiveTab,
      stateData.setActiveTab,
      stateData.setLogInfo,
      handleFailedTabClick,
      handleUnexecutedTabClick,
    ],
  )

  // 提取表格处理逻辑
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      console.log('pagination', pagination)
      console.log('filters', filters)
      console.log('sorter', sorter)
      stateData.setFilterParams(prev => {
        // 保留之前的筛选条件，只更新发生变化的筛选条件
        const newFilters = { ...prev }

        // 处理表头筛选条件
        Object.keys(filters).forEach(key => {
          if (filters[key] === null) {
            delete newFilters[key]
          } else {
            newFilters[key] = filters[key]
          }
        })

        // 保留filterFormValue的筛选条件
        if (stateData.filterFormValue?.field && stateData.filterFormValue?.value) {
          const processedValue =
            stateData.filterFormValue.field === 'tag_ids'
              ? (stateData.filterFormValue.value as string[])?.map(item =>
                  item.split('$').slice(1).join('$'),
                )
              : stateData.filterFormValue.value
          newFilters[stateData.filterFormValue.field] = processedValue
        }

        return {
          ...newFilters,
          page: pagination.current,
          page_size: pagination.pageSize,
          sort_order:
            sorter.order === 'descend'
              ? 'SORT_ORDER_DESCENDING'
              : sorter.order === 'ascend'
              ? 'SORT_ORDER_ASCENDING'
              : undefined,
          sort_field: 'sort_field_exec_time',
        }
      })
    },
    [stateData.filterFormValue, stateData.setFilterParams],
  )

  return (
    <DetailContainer>
      <DetailTemplate
        scene={displayModel.name}
        taskName={apiData.taskInfo.taskName}
        taskStatus={Number(apiData.taskInfo.isFinished)}
        canImport={apiData.importRecord.canImport}
        isImporting={apiData.importRecord.importing}
        taskContent={taskContentComponent}
        taskContentExpanded={taskContentExpanded}
        onTableChange={handleTableChange}
        taskCharts={
          <TaskCharts
            taskScore={apiData.taskInfo?.score}
            verifyResult={apiData.verifyDistribution}
            attackProtectionRanking={apiData.attackDefenseRanking}
            deviceProtectionRanking={apiData.deviceProtectionList}
            templateProtectionFailureRanking={apiData.templateLoseEfficacyRanking}
            riskLevelDistribution={apiData.riskLevelDistribution}
            caseLevelDistribution={apiData.resourceLevelsRanking}
          />
        }
        treeData={tagTreeData}
        handleExpand={handleExpand}
        filterOptions={options as any}
        filterFormValue={stateData.filterFormValue}
        setFilterFormValue={stateData.setFilterFormValue}
        filterParams={stateData.filterParams}
        setFilterParams={stateData.setFilterParams}
        columns={columns}
        dataSource={stateData.useCaseList}
        total={stateData.useCaseTotal}
        selectedRowKeys={stateData.selectedRowKeys}
        setSelectedRowKeys={stateData.setSelectedRowKeys}
        rowSelection={rowSelection}
        fixedCount={stateData.fixPayloadCount}
        fixedOptions={fixedOptions}
        handleFixed={values =>
          handleFixed(values, stateData.selectedRowKeys, apiData.form, apiData.getTaskInfo)
        }
        fixedLoading={apiData.updateInterceptStatusLoading}
        getEchartsData={getEchartsData}
        refreshRequest={refreshRequest}
        loading={apiData.loading}
        handleBack={handleBack}
        openRecordModal={stateData.setRecordVisible}
        openImportModal={stateData.setImportVisible}
        openReportModal={params => stateData.setReportVisible(prev => ({ ...prev, ...params }))}
        openDeleteModal={stateData.setDeleteVisible}
        fixedModalVisible={stateData.fixedModalVisible}
        setFixedModalVisible={stateData.setFixedModalVisible}
        scroll={{ x: 1200 }}
      />

      <RecordModal
        open={stateData.recordVisible}
        loading={apiData.deleteRecordLoading}
        dataSource={apiData.importRecord.logDeviceRecords}
        onClose={() => stateData.setRecordVisible(false)}
        handleDelete={deleteRecord}
      />

      <ImportModal
        open={stateData.importVisible}
        type={'WG'}
        resultId={apiData.id}
        refreshPage={() => {
          refreshRequest()
          getEchartsData()
          stateData.setRandom(Math.random())
        }}
        onClose={() => stateData.setImportVisible(false)}
        options={importModalOptions}
        deviceTypeValue={'TIP'}
      />

      <CreateReport
        visible={stateData.reportVisible.visible}
        createLoading={apiData.createLoading}
        templateType={2}
        onConfirm={e => {
          createReport(e, stateData.reportVisible.name)
        }}
        onCancel={() => {
          stateData.setReportVisible(origin => ({ ...origin, visible: false }))
        }}
      />

      <DeleteModal
        onOk={handleDelete}
        visible={stateData.deleteVisible}
        title={t('删除任务')}
        text={t('此操作将永久删除该任务,是否继续?')}
        onCancel={() => {
          stateData.setDeleteVisible(false)
        }}
      />

      <DetailDrawer
        onClose={() => {
          stateData.setDetailDrawerVisible(false)
        }}
        dataList={stateData.pluginDetail}
        logList={stateData.pluginDetail?.device_info}
        visible={stateData.detailDrawerVisible}
        loading={apiData.detailLoading}
      />
    </DetailContainer>
  )
}

export default Detail

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 16px;
  height: 100%;
  .case_name {
    &:hover {
      text-decoration: underline;
    }
  }

  .confidence-level {
    color: #86909c;
    font-size: 12px;

    span {
      margin-right: 12px;
    }
  }
`
